import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  useParticipantProperty,
  useLocalSessionId,
  useDaily,
  useAppMessage,
  useActiveParticipant,
} from '@daily-co/daily-react';
import {
  screenShareAtom,
  myNftAtom,
  sessionUsersAtom,
  nftIDAtom,
  pageAtom,
  webcamAtom,
  topMessageAtom,
  reactionAtom,
  nftDetailAtom,
  avatarSizeAtom,
  dailyStateAtom,
  customAvatarAtom,
} from '../../State';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as PIXI from 'pixi.js';
import { Assets } from '@pixi/assets';
import { useTranslation } from 'react-i18next';
import { getImagePath } from '../Util/utils';

//import { Live2DModel, MotionPreloadStrategy } from 'pixi-live2d-display';
//import { DotFilter, PixelateFilter, AdvancedBloomFilter } from 'pixi-filters';
import { Live2DModel, Cubism4ModelSettings } from 'pixi-live2d-display/cubism4';

import Live2dDetectMe from './Live2dDetectMe';
import Live2dDetectOther from './Live2dDetectOther';
import Live2dDetectVoiceMe from './Live2dDetectVoiceMe';
import Live2dDetectVoiceOther from './Live2dDetectVoiceOther';
import AudioStream from '../Avatar/AudioStream';
import DataModel from '../Util/DataModel';
import Reaction from './Live2dReaction';
import LoadingModel from './Live2dLoading';
import isElectron from '../../Global';
import getModelURL from './Live2dAvatarModel'; //モデルURLを取得
import { isMobile } from 'react-device-detect';
import { expressionData, motionData, motionData_kobayashi } from './Live2dData';
import { Ticker } from '@pixi/ticker';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const Live2dAvatar = React.memo(
  ({
    isMe,
    id,
    app,
    wrapWidthRef,
    avatarCount,
    eachWidthRef,
    canvasWidth,
    canvasHeight,
    index,
    isHairCheck,
    canvasRef,
    isFaceScreen = false,
    isRecord = false,
  }) => {
    const urlParam = useParams();
    var roomID = urlParam.id; //URL取得

    //Daily系
    const localSessionId = useLocalSessionId();
    if (isMe) {
      id = localSessionId;
      if (id == null) {
        id = '------';
      }
    }
    const userName = useParticipantProperty(id, 'user_name');
    const isMicOn = useParticipantProperty(id, 'audio');
    const callObject = useDaily();
    const sendAppMessage = useAppMessage();
    const activeSpeaker = useActiveParticipant(); //アクティブスピーカー
    const { t, i18n } = useTranslation();

    //Recoil系
    const [customAvatarState, setCustomAvatarState] = useRecoilState(customAvatarAtom);
    const screenShareState = useRecoilValue(screenShareAtom);
    const myNftState = useRecoilValue(myNftAtom); //NFTアイコン
    const sessionUsers = useRecoilValue(sessionUsersAtom);
    const setNftDetail = useSetRecoilState(nftIDAtom);
    const setPageState = useSetRecoilState(pageAtom);
    const [webcamState] = useRecoilState(webcamAtom);
    const setTopMessageState = useSetRecoilState(topMessageAtom);
    const reactionState = useRecoilValue(reactionAtom);
    const setNftDetailPageState = useSetRecoilState(nftDetailAtom);
    const [avatarSizeState, setAvatarSizeState] = useRecoilState(avatarSizeAtom);

    //各種リファレンス
    const containerRef = useRef(null);
    const baseRef = useRef(null);
    const modelRef = useRef(null);
    const nameBoxRef = useRef(null);
    const nameRef = useRef(null);
    const muteButtonRef = useRef(null);
    const recordingRef = useRef(null);
    const whoCanUseRef = useRef(null);
    const changeSizeRef = useRef(null);
    const loadingRef = useRef(null);
    const [isRecordingState, setIsRecordingState] = useState(false);
    const isRecording = useRef(false);
    const [isMuteState, setIsMuteState] = useState(false);
    const isMute = useRef(false);
    const [nftIcon, setNftIcon] = useState(null); //顔のベース画像
    const displayNameRef = useRef(null);
    const userIDRef = useRef(null);
    const nftIDRef = useRef(null);
    const nftWhoCanUseRef = useRef('anyone');
    const [isFaceTracking, setIsFaceTracking] = useState(false);
    const isFirstRun = useRef(true); //初回ロードかのチェック
    const isFirstLoad = useRef(false); //初回ロードかのチェック
    const isScreenShare = useRef(false);
    const avatarSizeRef = useRef(1);
    const workerRef = useRef(null);
    const tickerRef = useRef(null);

    //PIXIJSのRetina対策用
    const scale = window.devicePixelRatio || 1;

    //初期化
    useEffect(() => {
      //ブラウザバックグランド処理
      setBackgroundProcess();

      //アバター作成
      makeAvatar();

      //Tickerを設定
      setUpTicker();

      //アバター削除
      return () => {
        if (containerRef.current != null) {
          containerRef.current.parent.removeChild(containerRef.current);
        }
      };
    }, []);

    //Tickerを設定
    function setUpTicker() {
      tickerRef.current = new Ticker();
      tickerRef.current.add(() => {
        if (modelRef.current != null) {
          modelRef.current.update(tickerRef.current.elapsedMS);
        }
      });
      tickerRef.current.start();
    }

    //初期化
    useEffect(() => {
      adjustAvatar();
    }, [isHairCheck]);

    //調整
    useEffect(() => {
      if (modelRef.current != null) {
        //時間を少しあけないと、大本のサイズが取得できない
        setTimeout(() => {
          adjustAvatar();
        }, 50);
      }

      //スクリーンシェアのときは名前を隠す
      setTimeout(() => {
        if (isScreenShare.current) {
          //名前を隠す
          showName(false);
        } else {
          //名前を表示
          showName(true);
        }
      }, 10);
    }, [canvasWidth, canvasHeight, avatarCount, screenShareState]);

    //初期化
    useEffect(() => {
      if (modelRef.current != null && isScreenShare.current) {
        if (avatarSizeState == 'normal') {
          avatarSizeRef.current = 0;
        } else if (avatarSizeState == 'size2') {
          avatarSizeRef.current = 1;
        } else {
          avatarSizeRef.current = 2;
        }

        adjustAvatar();
      }
    }, [avatarSizeState]);

    //カスタムアバター
    useEffect(() => {
      if (isMe) {
        if (Object.keys(customAvatarState).length > 0) {
          //新しくモデルを読み込み
          console.log('カスタムモデルロード');
          //モデルを削除
          containerRef.current.parent.removeChild(containerRef.current);

          //各種値をリセット
          modelRef.current = null;
          containerRef.current = null;
          baseRef.current = null;
          nameRef.current = null;
          nameBoxRef.current = null;
          muteButtonRef.current = null;
          recordingRef.current = null;
          whoCanUseRef.current = null;
          changeSizeRef.current = null;

          //ローディングを表示
          loadingRef.current = null;

          //モデルを新規作成
          makeAvatar(customAvatarState.base);
        } else {
          if (isFirstRun.current == false) {
            loadNewModel(myNftState);
          }
        }
      }
    }, [JSON.stringify(customAvatarState)]);

    //スクリーンシェアバグ対策 (refで参照しないとだめな場合がある)
    useEffect(() => {
      if (screenShareState == null) {
        isScreenShare.current = false;
      } else {
        isScreenShare.current = true;
      }
    }, [screenShareState]);

    //名前の変更
    useEffect(() => {
      if (userName != null && nameRef.current != null) {
        nameRef.current.text = userName;
        adjustAvatar();
      }
    }, [userName]);

    //ミュートボタンの変更
    useEffect(() => {
      //if (isFirstLoad.current == false) return;

      //自分の場合
      if (isMe) {
        if (isMicOn == false) {
          isMute.current = true;
        } else {
          isMute.current = false;
        }
      }
      //他の人の場合
      else {
        if (isMuteState == false) {
          isMute.current = false;
        } else {
          isMute.current = true;
        }
      }

      //位置調整
      adjustAvatar();
    }, [isMicOn, isMuteState]);

    //録画中の変更
    useEffect(() => {
      //if (modelRef.current != null) {
      //makeRecordingButton();
      if (isRecordingState) {
        isRecording.current = true;
      } else {
        isRecording.current = false;
      }
      //位置調整
      adjustAvatar();
      //}
    }, [isRecordingState]);

    //NFTアイコンの変更
    useEffect(() => {
      if (isMe) {
        if (myNftState != nftIcon && myNftState != null) {
          //自分用に設定
          setNftIcon(myNftState);

          //新しくモデルを読み込み
          loadNewModel(myNftState);
        }
      }
    }, [myNftState]);

    //アクティブスピーカー
    useEffect(() => {
      //スクリーンシェア中は無効に
      if (nameBoxRef.current != null && isScreenShare.current == false) {
        if (activeSpeaker != null) {
          if (activeSpeaker.session_id == id) {
            nameBoxRef.current.tint = 0x5724ff;

            //一番前のレイヤーに
            containerRef.current.zIndex = 10;
          } else {
            nameBoxRef.current.tint = 0x191919;
            containerRef.current.zIndex = index;
          }
        }
      }
    }, [activeSpeaker]);

    //自分のカメラがオフの時は新しくモデルをロードしないとまばたきが動かない
    useEffect(() => {
      //初回ロードの場合はスキップ
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }

      //瞬きの設定
      if (isMe) {
        const coreModel = modelRef.current.internalModel.coreModel;
        coreModel.setParameterValueById('ParamEyeLOpen', 1);
        coreModel.setParameterValueById('ParamEyeROpen', 1);
      }

      //データベースに保存
      if (isMe && callObject != null) {
        callObject
          .getMeetingSession()
          .then(async (session) => {
            try {
              //自分の情報をseessionに登録
              await DataModel.saveSessionToRoom(
                null, //ルームID
                session.meetingSession.id, //ルームのセッションID
                localSessionId, //ユーザーのテンポラリーユーザーID
                null, //顔
                null, //背景
                webcamState, //顔認識 <isFaceTrackingå>
                null, //録画
                null, //ミュート
              );

              console.log('セッション保存完了');
            } catch (error) {
              console.log(error);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }

      //loadNewModel(null);
    }, [webcamState]);

    //相手のカメラがオフの時は新しくモデルをロードしないとまばたきが動かない
    useEffect(() => {
      // if (isMe == false && isFaceTracking == false) {
      //   loadNewModel(null);
      // }
    }, [isFaceTracking]);

    //セッション情報を元に更新
    useEffect(() => {
      if (Object.keys(sessionUsers).length > 0) {
        //オブジェクトとしてユーザーの処理
        Object.keys(sessionUsers).forEach(function (key) {
          var user = sessionUsers[key];

          //自分の場合で録画中かのチェック
          if (user.tempUserID == id && isMe) {
            //録画中をセット
            if (user.isRecording != null) {
              if (user.isRecording) {
                setIsRecordingState(true);
              } else {
                setIsRecordingState(false);
              }
            } else {
              if (isRecordingState) {
                setIsRecordingState(false);
              }
            }

            //ユーザーIDをセット
            if (user.userID != null) {
              userIDRef.current = user.userID;
            }

            //ディスプレイ名
            let myUserName;
            if (isMe) {
              if (localStorage.getItem('displayName') != null) {
                myUserName = localStorage.getItem('displayName');
              } else {
                myUserName = user.displayName;
              }
            }

            displayNameRef.current = myUserName;
          }

          //自分ではない場合のみ処理
          if (user.tempUserID == id && isMe == false) {
            //NFT画像を変更
            if (user.nftID != null) {
              setNftIcon(user.nftID);

              if (user.nftID != nftIcon) {
                //新しいモデルをロード
                loadNewModel(user.nftID);
              }
            } else {
              setNftIcon(null);
            }

            //ユーザーIDをセット
            if (user.userID != null) {
              userIDRef.current = user.userID;
            }

            //ディスプレイ名
            if (user.displayName != null) {
              displayNameRef.current = user.displayName;
            }

            //NFTIDをセット
            if (user.nftID != null) {
              nftIDRef.current = user.nftID;
            }

            //NFT WhoCanUseをセット
            if (user.nftWhoCanUse != null) {
              nftWhoCanUseRef.current = user.nftWhoCanUse;
            }

            //録画中をセット
            if (user.isRecording != null) {
              if (user.isRecording) {
                if (isRecordingState == false) {
                  setTopMessageState({
                    type: 'joined',
                    message: t('top.Recording Started'),
                  });
                }

                setIsRecordingState(true);
              } else {
                setIsRecordingState(false);
              }
            } else {
              if (isRecordingState) {
                setIsRecordingState(false);
              }
            }

            //ミュート
            if (user.isMute != null) {
              setIsMuteState(user.isMute);
            }

            //カメラ認識
            if (user.isFaceTracking != null) {
              setIsFaceTracking(user.isFaceTracking);

              if (isFaceTracking != user.isFaceTracking && modelRef.current != null) {
                const coreModel = modelRef.current.internalModel.coreModel;
                coreModel.setParameterValueById('ParamEyeLOpen', 1);
                coreModel.setParameterValueById('ParamEyeROpen', 1);
              }
            }
          }
        });
      }
    }, [sessionUsers]);

    //バックグランド処理のリスナー
    function setBackgroundProcess() {
      // ページの表示状態が変わったときに実行
      document.addEventListener('visibilitychange', function () {
        let status = document.visibilityState;

        //ブラウザがバックグランドへ行った
        if (status == 'hidden') {
          if (workerRef.current != null) {
            workerRef.current.terminate();
            workerRef.current = null;
          }
          workerRef.current = new Worker('js/worker.js');

          //Workerにメッセージを送信(Worker処理開始)
          workerRef.current.postMessage('start');

          workerRef.current.addEventListener(
            'message',
            function (e) {
              let diff = e.data;
              if (modelRef.current != null) modelRef.current.update(diff);
            },
            false,
          );
        }
        //ブラウザが再度アクティブになった
        else {
          if (workerRef.current != null) {
            workerRef.current.terminate();
            workerRef.current = null;
          }
        }
      });
    }

    function makeMultiple() {
      if (avatarCount == 1) {
        return 1;
      } else if (avatarCount == 2) {
        return 0.95;
      } else if (avatarCount == 3) {
        return 0.9;
      } else if (avatarCount == 4) {
        return 0.85;
      } else if (avatarCount == 5) {
        return 0.85;
      } else if (avatarCount == 6) {
        return 0.85;
      } else {
        return 0.85;
      }
    }

    //ローディングを表示
    async function makeLoading() {
      try {
        var result = await LoadingModel(loadingRef, containerRef);
        if (result) {
          adjustAvatar();
        }
      } catch (e) {}
    }

    //アバターの作成
    async function makeAvatar(newNft) {
      //コンテイナー
      if (containerRef.current == null) {
        containerRef.current = new PIXI.Container();
        containerRef.current.sortableChildren = true;

        containerRef.current.zIndex = index;
        app.current?.stage.addChild(containerRef.current);
      }

      //スプライト追加
      if (baseRef.current == null) {
        baseRef.current = new PIXI.Sprite();
        containerRef.current.addChild(baseRef.current);

        //検知機能ON
        baseRef.current.interactive = true;

        //マイスポインター
        baseRef.current.buttonMode = true;
        //baseRef.current.hitArea = new PIXI.Circle(150, 150, 100);

        //ジェスチャー追加
        // baseRef.current.click = function () {
        //   onClick();
        // };

        baseRef.current.on('pointertap', (event) => {
          onClick();
        });

        //
        baseRef.current.mouseout = function () {
          onMouseOut();
        };

        /******* ローディング追加 ************/
        makeLoading();

        /******* テキストの追加 ************/
        if (isFaceScreen == false) {
          makeName();
        }

        /******* ミュートボタンの追加 ************/
        makeMuteButton();

        /******* 録画中ボタンの追加 ************/
        makeRecordingButton();

        /******* 誰が使えるかアイコンの追加 ************/
        makeWhoCanUseIcon();

        /******* スクリーン中のサイズ変更ボタン ************/
        makeSizeChangeButton();

        /******* 位置調整1回目 ************/
        adjustAvatar();
      }

      //モデルの読み込みをまだしていない場合
      if (modelRef.current == null) {
        //モデルの読み込み用URLの取得
        let modelURL;
        if (newNft != null) {
          modelURL = getModelURL(newNft);
        } else if (nftIcon != null) {
          modelURL = getModelURL(nftIcon);
        }
        //録画専用とエレクトロン時のフェイス選択画面の場合
        else if (isFaceScreen || isRecord) {
          if (localStorage.getItem('nftID') == null) {
            modelURL = getModelURL('defaultkun');
          } else {
            modelURL = getModelURL(localStorage.getItem('nftID'));
          }
        }

        //モデル用URLがNullではない場合
        if (modelURL != null) {
          let json;
          var isCustom = false;
          let customResult;
          let avatarState;

          //カスタムアバターの場合
          if (modelURL.startsWith('custom')) {
            try {
              customResult = await DataModel.getCustomJson(modelURL);
              json = customResult.json;
              isCustom = true;
            } catch (error) {
              console.log('カスタムアバター取得エラー');
            }
          }
          //通常アバターの場合
          else {
            json = await fetch(modelURL).then((res) => res.json());
            //urlパラメーターを追加
            json.url = modelURL;
          }

          //オブジェクトが空かどうかをチェック
          if (Object.keys(customAvatarState).length > 0 || isCustom) {
            if (isCustom) {
              avatarState = customResult.avatarData;
            } else {
              avatarState = customAvatarState;
            }

            let b = avatarState.body;
            json.FileReferences.Moc = b + '/base/' + avatarState.hair + '.moc3';
            json.FileReferences.Physics = b + '/base.physics3.json';
            json.FileReferences.DisplayInfo = b + '/base.cdi3.json';

            json.FileReferences.Textures[0] = b + '/body/body.png';
            json.FileReferences.Textures[1] = b + '/hair/' + avatarState.hair + '.png';
            json.FileReferences.Textures[2] = b + '/eyebrow/' + avatarState.eyebrow + '.png';
            json.FileReferences.Textures[3] = b + '/eye/baseeye.png';
            json.FileReferences.Textures[4] = b + '/eyepupil/' + avatarState.eye + '.png';

            json.FileReferences.Textures[5] = b + '/mouth/' + avatarState.mouth + '.png';
            json.FileReferences.Textures[6] = b + '/beard/' + avatarState.beard + '.png';
            json.FileReferences.Textures[7] = b + '/glass/' + avatarState.glass + '.png';
            json.FileReferences.Textures[8] = b + '/cloth/' + avatarState.cloth + '.png';
            json.FileReferences.Textures[9] = b + '/special/' + avatarState.special + '.png';

            //json.FileReferences = {
            //Moc: 'base/M0.moc3',
            //Textures: ['face/M0.png'],
            //Physics: 'base.physics3.json',
            //DisplayInfo: 'base.cdi3.json',
            //};
          }

          //モーションの設定の上書き
          if (roomID == 'makopt') {
            var motionJson = 'https://faceless.hiho.jp/kobayashi-motions/motion-list2.json';

            await fetch(motionJson, { method: 'GET' })
              .then((res) => res.json())
              .then((data) => {
                console.log(data);
                json.FileReferences.Motions = data;
              });
          } else {
            json.FileReferences.Motions = motionData;
          }

          //エクスプレッションの設定
          json.FileReferences.Expressions = expressionData;

          /*
          //セッティングを生成
          const settings = new Cubism4ModelSettings(json);

          //ファイルの書き換え（必要であれば行う）
          settings.replaceFiles((file, path) => {
            //pathでファイル拡張子が確認できて、そこで参照するファイル名の書き換えができる
            if (path == 'moc') {
              // file = 'foo.moc';
            }
            // file = "foo.moc", path = "moc"
            // file = "foo.png", path = "textures[0]"
            // file = "foo.mtn", path = "motions.idle[0].file"
            // file = "foo.motion3.json", path = "motions.idle[0].File"
            //returnで返したものがファイル名になる
            return file;
          });
          */

          /******* モデルの追加 ************/
          let modelTempRef = await Live2DModel.from(json, {
            autoInteract: false, //マウスカーソルを顔を追うかどうか
            autoUpdate: false, //自動でアップデートするのをfalseに(Tickerを使ったマニュアル更新)
          });

          //二重でモデルが読み込まれてしまうのを防ぐ
          if (modelRef.current != null) {
            modelRef.current.parent.removeChild(modelRef.current);
          }

          modelRef.current = modelTempRef;

          //一旦非表示に
          modelRef.current.visible = false;

          //コンテイナーに追加
          containerRef.current.addChild(modelRef.current);

          //ローディング画像を非表示
          if (loadingRef.current != null) {
            loadingRef.current.visible = false;
          }

          //アクティングモードに
          if ((isMe && webcamState == false) || (isMe == false && isFaceTracking == false)) {
            modelRef.current.internalModel.motionManager.groups.idle = 'Acting';
          }

          //カスタム用（目のパラメーター更新）
          if (avatarState != null) {
            let eyePattern = avatarState.eye;
            var eyeParm = 1;

            if (eyePattern == 'e2') {
              eyeParm = 2;
            }
            if (eyePattern == 'e3') {
              eyeParm = 3;
            }
            if (eyePattern == 'e4') {
              eyeParm = 4;
            }
            if (eyePattern == 'e5') {
              eyeParm = 5;
            }

            modelRef.current.internalModel.coreModel.setParameterValueById(
              'eyepatternParam',
              eyeParm,
            );
          }

          //modelRef.current.internalModel.coreModel.setParameterValueById('hairColor', 1);

          // let hei = modelRef.current.internalModel.coreModel.getCanvasHeight();

          //modelRef.current.internalModel.coreModel.setColor();

          // modelRef.current.internalModel.coreModel.settings.replaceFiles((file, path) => {
          //   // file = "foo.moc", path = "moc"
          //   // file = "foo.png", path = "textures[0]"
          //   // file = "foo.mtn", path = "motions.idle[0].file"
          //   // file = "foo.motion3.json", path = "motions.idle[0].File"
          //   //return 'bar/' + file;
          //   console.log(file);

          //   if (file == 'test.1024/texture_00.png') {
          //     // alert('hi');
          //     return 'test.1024/texture_01.png';
          //   }

          //   return file;
          // });

          // modelRef.current.internalModel.coreModel.update();

          // let gl = canvasRef.current.getContext('webgl');

          // console.log('ここ');
          // console.log(gl);
          //let cubeTexture = gl.createTexture();
          // let cubeImage = new Image();
          // cubeImage.onload = function () {
          //   // handleTextureLoaded(cubeImage, cubeTexture);
          //   modelRef.current.internalModel.bindTexture(0, cubeTexture);
          // };
          // cubeImage.src = '/live2dmodel/test2/test.1024/texture_01.png';

          // let loadURL = '/live2dmodel/test2/test.1024/texture_01.png';
          // const texture = await Assets.load(loadURL);
          // modelRef.current.internalModel.bindTexture(0, texture);

          /******* 位置調整２回目 ************/
          adjustAvatar();

          //一秒後に遅延処理
          setTimeout(async () => {
            //初回ロード完了
            isFirstLoad.current = true;

            // console.log('ここきた');

            // var status = await modelRef.current.motion(
            //   'AnimGroup',
            //   undefined,
            //   MotionPriority.FORCE,
            // );
            // var status = await modelRef.current.internalModel.motionManager.startRandomMotion(
            //   'AnimGroup',
            // );

            //console.log(status);

            //console.log(modelRef.current.ticker.FPS);
          }, 3000);
        }
      }
    }

    //名前を追加
    function makeName() {
      let myUserName;
      if (isMe) {
        if (localStorage.getItem('displayName') != null) {
          myUserName = localStorage.getItem('displayName');
        } else {
          myUserName = id?.substring(0, 10);
        }
      } else {
        myUserName = userName;
      }

      //テキストボックス追加
      const g = new PIXI.Graphics();
      g.beginFill(0x191919);
      g.drawRect(0, 0, 10, 10);
      g.endFill();

      // //テキスチャに変換
      let t = app.current?.renderer.generateTexture(g);

      let tt = PIXI.Texture.WHITE;
      nameBoxRef.current = new PIXI.Sprite(tt);
      nameBoxRef.current.tint = 0x191919;
      containerRef.current.addChild(nameBoxRef.current);

      //テキスト追加
      nameRef.current = new PIXI.Text(myUserName, { fill: 0xffffff });
      containerRef.current.addChild(nameRef.current);
    }

    //モデルのロード
    async function loadModel(modelURL) {
      // modelRef.current.internalModel.motionManager.on('motionStart', (group, index, audio) => {
      //   console.log('モーション開始');
      //   console.log(group);
      // });
      //modelRef.current.internalModel.motionManager.groups.idle = 'MyBlink';
    }

    //ミュートボタンの追加
    async function makeMuteButton() {
      if (muteButtonRef.current == null) {
        // let loadURL;
        // if (isElectron) {
        //   loadURL = './images/avatar/muteSmallButton.png';
        // } else {
        //   loadURL = '../../images/avatar/muteSmallButton.png';
        // }

        let loadURL = getImagePath() + '/avatar/muteSmallButton.png';

        const texture = await Assets.load(loadURL);
        muteButtonRef.current = new PIXI.Sprite(texture);
        containerRef.current.addChild(muteButtonRef.current);
        muteButtonRef.current.visible = false;
        //位置調整
        //adjustAvatar();
      }
    }

    //録画中ボタン
    async function makeRecordingButton() {
      // const loader = new PIXI.Loader();
      // loader.add('recordingButton', './images/avatar/recordingNow.png');
      // loader.load((_, resources) => {

      // let loadURL;
      // if (isElectron) {
      //   loadURL = './images/avatar/recordingNow.png';
      // } else {
      //   loadURL = '../../images/avatar/recordingNow.png';
      // }

      let loadURL = getImagePath() + '/avatar/recordingNow.png';
      const texture = await Assets.load(loadURL);
      recordingRef.current = new PIXI.Sprite(texture);
      containerRef.current.addChild(recordingRef.current);

      recordingRef.current.visible = false;

      //位置調整
      // adjustAvatar();
      //  });
    }

    //サイズ変更ボタン
    async function makeSizeChangeButton() {
      var loadURL = '';

      if (i18n.language == 'ja') {
        loadURL = getImagePath() + '/icons/changeSizeJP.png';
      } else {
        loadURL = getImagePath() + '/icons/changeSizeEN.png';
      }

      const texture = await Assets.load(loadURL);
      changeSizeRef.current = new PIXI.Sprite(texture);
      containerRef.current.addChild(changeSizeRef.current);
      changeSizeRef.current.zIndex = 2;
      changeSizeRef.current.visible = false;
    }

    //誰が使えるかマーク
    async function makeWhoCanUseIcon() {
      var nftWhoCanUseFlg = 'anyone';
      var loadURL = '';
      if (isMe && localStorage.getItem('nftWhoCanUse') !== null) {
        nftWhoCanUseFlg = localStorage.getItem('nftWhoCanUse');
      } else {
        if (nftWhoCanUseRef.current != null) {
          nftWhoCanUseFlg = nftWhoCanUseRef.current;
        }
      }

      //ホルダーかユニークホルダーの時
      if (nftWhoCanUseFlg == 'allHolders' || nftWhoCanUseFlg == 'uniqueHolder') {
        if (nftWhoCanUseFlg == 'allHolders') {
          loadURL = getImagePath() + '/avatar/allHoldersIcon.png';

          // if (isElectron) {
          //   loadURL = './images/avatar/allHoldersIcon.png';
          // } else {
          //   loadURL = '../../images/avatar/allHoldersIcon.png';
          // }
        } else if (nftWhoCanUseFlg == 'uniqueHolder') {
          loadURL = getImagePath() + '/avatar/uniqueHolderIcon.png';
          // if (isElectron) {
          //   loadURL = './images/avatar/uniqueHolderIcon.png';
          // } else {
          //   loadURL = '../../images/avatar/uniqueHolderIcon.png';
          // }
        }

        // loader.add('whoCanUseIcon', loadURL);
        // loader.load((_, resources) => {
        const texture = await Assets.load(loadURL);
        whoCanUseRef.current = new PIXI.Sprite(texture);
        containerRef.current.addChild(whoCanUseRef.current);
        whoCanUseRef.current.visible = false;
        // });
      }
    }

    //クリックされた時
    function onClick() {
      // var myName = '';
      // //自分の場合はローカルストレージの名前を先に確認
      // if (isMe && localStorage.getItem('displayName') != null) {
      //   myName = localStorage.getItem('displayName');
      // } else if (userName != null) {
      //   myName = userName;
      // }

      //スクリーンシェア中で自分の場合はアバターサイズ変更
      if (isScreenShare.current && isMe) {
        if (avatarSizeRef.current == 0) {
          avatarSizeRef.current = 2;
          setAvatarSizeState('size3');
        } else if (avatarSizeRef.current == 1) {
          avatarSizeRef.current = 0;
          setAvatarSizeState('normal');
        } else {
          avatarSizeRef.current = 1;
          setAvatarSizeState('size2');
        }

        return;
      }

      var nftDetailID = null;
      var nftWhoCanUse = 'anyone';

      //自分の場合はローカルストレージを先に確認
      if (isMe && localStorage.getItem('nftID') != null) {
        nftDetailID = localStorage.getItem('nftID');
      } else {
        if (nftIDRef.current != null) {
          nftDetailID = nftIDRef.current;
        } else {
          nftDetailID = null;
        }
      }

      //自分の場合はローカルストレージを先に確認
      if (isMe && localStorage.getItem('nftWhoCanUse') != null) {
        nftWhoCanUse = localStorage.getItem('nftWhoCanUse');
      } else {
        if (nftWhoCanUseRef.current != null) {
          nftWhoCanUse = nftWhoCanUseRef.current;
        }
      }

      setNftDetailPageState({
        show: true,
        isMe: isMe,
        displayName: displayNameRef.current,
        userID: userIDRef.current,
        nftID: nftDetailID,
        nftWhoCanUse: nftWhoCanUse,
      });

      // //詳細用にNFTをセット
      // setNftDetail({
      //   userName: myName,
      //   nftID: nftDetailID,
      //   isMe: isMe,
      // });

      // //ページを切り替え
      // setPageState('nftDetail');
    }

    //マウスオーバーした時
    function onMouseOver() {
      if (modelRef.current == null) return;

      let filter;
      if (isScreenShare.current) {
        filter = new PIXI.filters.AlphaFilter(0.4);
        //フィルターを指定（複数指定可能)
        modelRef.current.filters = [filter];

        //サイズ変更ボタンの表示
        if (isMe) changeSizeRef.current.visible = true;
      } else {
        filter = new PIXI.filters.AlphaFilter(0.8);

        //filter = new PIXI.filters.BlurFilter();
        //filter = new DotFilter();
        //filter = new PixelateFilter(5);
        modelRef.current.filters = [filter];
      }
    }

    //マウスが外れた時
    function onMouseOut() {
      if (modelRef.current == null) return;
      //フィルターをはずして通常表示へ
      modelRef.current.filters = [];
      //サイズ変更ボタン非表示
      changeSizeRef.current.visible = false;
    }

    //名前の表示・非表示
    function showName(status = true) {
      if (nameRef.current != null) {
        //表示
        if (status) {
          nameRef.current.visible = true;
          nameBoxRef.current.visible = true;
          if (muteButtonRef.current != null && isMute.current) {
            console.log('ここきちゃう');
            muteButtonRef.current.visible = true;
          }

          if (recordingRef.current != null && isRecording.current) {
            recordingRef.current.visible = true;
          }

          if (whoCanUseRef.current != null) {
            whoCanUseRef.current.visible = true;
          }
        }
        //非表示
        else {
          nameRef.current.visible = false;
          nameBoxRef.current.visible = false;
          if (loadingRef.current != null) {
            loadingRef.current.visible = false;
          }
          if (muteButtonRef.current != null) {
            muteButtonRef.current.visible = false;
          }

          if (recordingRef.current != null) {
            recordingRef.current.visible = false;
          }

          if (whoCanUseRef.current != null) {
            whoCanUseRef.current.visible = false;
          }
        }
      }
    }

    //新しいモデルをロード（リセット後）
    function loadNewModel(newNft) {
      console.log('ロード');
      //モデルを削除
      containerRef.current.parent.removeChild(containerRef.current);

      //各種値をリセット
      modelRef.current = null;
      containerRef.current = null;
      baseRef.current = null;
      nameRef.current = null;
      nameBoxRef.current = null;
      muteButtonRef.current = null;
      recordingRef.current = null;
      whoCanUseRef.current = null;
      changeSizeRef.current = null;
      //tickerRef.current = null;

      //ローディングを表示
      loadingRef.current = null;

      //モデルを新規作成
      makeAvatar(newNft);
    }

    /*--------------------------------- 位置調整開始 --------------------------------------- */

    //位置、サイズ、アンカーの調整
    function adjustAvatar() {
      var eachWidth = isScreenShare.current ? eachWidthRef.current * 0.5 : eachWidthRef.current;
      let startPoint = isScreenShare.current
        ? eachWidth * 0.5
        : (canvasRef.current.offsetWidth ?? 0) / 2;
      let wrapWidth = wrapWidthRef.current;

      //フェイス選択画面の場合は固定
      if (isFaceScreen && isElectron) {
        eachWidth = 350;
      }

      //スクリーンシェア中（アバターサイズ）
      if (isScreenShare.current) {
        if (avatarSizeRef.current == 0) {
          eachWidth = eachWidth;
        } else if (avatarSizeRef.current == 1) {
          eachWidth = eachWidth * 1.2;
        } else if (avatarSizeRef.current == 2) {
          eachWidth = eachWidth * 1.4;
        }

        eachWidth = eachWidth * makeMultiple();
      }

      baseRef.current.width = eachWidth;
      baseRef.current.height = eachWidth;
      baseRef.current.anchor.set(0.5, 1);
      baseRef.current.y = app.current?.view.height / scale;

      //一人の場合
      let x;
      //スクリーンシェア中
      if (isScreenShare.current) {
        if (isMobile) {
          if (index == 0) {
            x = startPoint;
          } else {
            //アバターサイズ
            if (avatarSizeRef.current == 1) {
              x = startPoint + (index * eachWidth) / 1 / 1.2;
            } else if (avatarSizeRef.current == 2) {
              x = startPoint + (index * eachWidth) / 1.2 / 1.2;
            } else {
              x = startPoint + (index * eachWidth) / 1.5;
            }
            // x = startPoint + (index * eachWidth) / 1.5;
          }
        } else {
          //自分の場所だけ特殊だが指定
          if (index == 0) {
            x = startPoint;
          } else {
            //アバターサイズ
            if (avatarSizeRef.current == 1) {
              x = startPoint + (index * eachWidth) / 1.1 / 1.5;
            } else if (avatarSizeRef.current == 2) {
              x = startPoint + (index * eachWidth) / 1.2 / 1.5;
            } else {
              x = startPoint + (index * eachWidth) / 1.5;
            }
            // x = startPoint + (index * eachWidth) / 1.5;
          }
        }
      }
      //通常時
      else {
        //モバイルのとき
        if (isMobile) {
          if (avatarCount == 1) {
            x = startPoint;
          } else if (avatarCount == 2) {
            x =
              startPoint -
              wrapWidth / avatarCount +
              (index * wrapWidth) / avatarCount +
              wrapWidth * 0.25;
          } else if (avatarCount == 3) {
            x = startPoint - (wrapWidth / avatarCount) * 1 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 4) {
            x = startPoint - (wrapWidth / avatarCount) * 1.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 5) {
            x = startPoint - (wrapWidth / avatarCount) * 2 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 6) {
            x = startPoint - (wrapWidth / avatarCount) * 2.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 7) {
            x = startPoint - (wrapWidth / avatarCount) * 3 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 8) {
            x = startPoint - (wrapWidth / avatarCount) * 3.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 9) {
            x = startPoint - (wrapWidth / avatarCount) * 4 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 10) {
            x = startPoint - (wrapWidth / avatarCount) * 4.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 11) {
            x = startPoint - (wrapWidth / avatarCount) * 5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 12) {
            x = startPoint - (wrapWidth / avatarCount) * 5.5 + (index * wrapWidth) / avatarCount;
          }
        }
        //パソコンの場合
        else {
          if (avatarCount == 1) {
            //ヘアチェックのときだけずらす
            if (isHairCheck) {
              x = startPoint - eachWidth / 2;
            }
            //中央からスタート
            else {
              x = startPoint;
            }
          } else if (avatarCount == 2) {
            //x = startPoint - wrapWidth / avatarCount / 2 + index * eachWidth;
            if (index == 0) {
              x = startPoint - eachWidth * 0.5;
            } else {
              x = startPoint + eachWidth * 0.5;
            }
          } else if (avatarCount == 3) {
            x = startPoint - (wrapWidth / avatarCount) * 1 + (index * wrapWidth) / avatarCount;

            console.log('開始' + String(startPoint));
            console.log('開始' + String(wrapWidth));
            console.log('データ' + String(index) + ':' + x);
          } else if (avatarCount == 4) {
            x = startPoint - (wrapWidth / avatarCount) * 1.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 5) {
            x = startPoint - (wrapWidth / avatarCount) * 2 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 6) {
            x = startPoint - (wrapWidth / avatarCount) * 2.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 7) {
            x = startPoint - (wrapWidth / avatarCount) * 3 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 8) {
            x = startPoint - (wrapWidth / avatarCount) * 3.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 9) {
            x = startPoint - (wrapWidth / avatarCount) * 4 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 10) {
            x = startPoint - (wrapWidth / avatarCount) * 4.5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 11) {
            x = startPoint - (wrapWidth / avatarCount) * 5 + (index * wrapWidth) / avatarCount;
          } else if (avatarCount == 12) {
            x = startPoint - (wrapWidth / avatarCount) * 5.5 + (index * wrapWidth) / avatarCount;
          }
        }
      }

      //ベースのXの変更
      baseRef.current.x = x;

      //各種値
      let defaultWidth = 500; //基準のサイズ
      let aspect = eachWidthRef.current / defaultWidth;
      var scaleMulti = 1.2;

      // console.log('スケール');
      // console.log(scale);
      // console.log(scaleMulti);

      //サイズ変更
      if (isScreenShare.current) {
        if (avatarSizeRef.current == 1) {
          scaleMulti = 0.7;
          x = x * 1.2;
        } else if (avatarSizeRef.current == 2) {
          scaleMulti = 1;
          x = x * 1.4;
        } else {
          scaleMulti = 0.5;
        }

        x = x * makeMultiple();

        scaleMulti = scaleMulti * makeMultiple();
      }

      //モデル
      if (modelRef.current != null) {
        if (isFaceScreen) {
          modelRef.current.scale.set((eachWidth / defaultWidth) * scaleMulti);
        } else {
          modelRef.current.scale.set((eachWidthRef.current / defaultWidth) * scaleMulti);
        }

        modelRef.current.anchor.set(0.5, 1);
        modelRef.current.position.set(x, baseRef.current.position.y);

        //フリップする方法
        //modelRef.current.scale.x *= -1;

        modelRef.current.visible = true;
      }

      //ローディング
      if (loadingRef.current != null) {
        loadingRef.current.scale.set((eachWidthRef.current / defaultWidth) * 0.6);
        loadingRef.current.position.set(x, baseRef.current.position.y - (eachWidth / 2) * 1.3);

        if (modelRef.current != null) {
          loadingRef.current.visible = false;
        }
      }

      //名前エリア
      if (nameRef.current != null) {
        //名前
        nameRef.current.scale.set((eachWidthRef.current / defaultWidth) * 0.6);
        nameRef.current.anchor.set(0.5, 0.5);
        nameRef.current.position.set(x, baseRef.current.position.y - eachWidth * 1.25);

        //名前ボックス
        nameBoxRef.current.anchor.set(0.5, 0.5);

        //名前のWidth
        var nameWidth = 40;
        if (nameRef.current.width > 40) {
          nameWidth = nameRef.current.width;
        }

        nameBoxRef.current.width = nameWidth + 30 * aspect;
        nameBoxRef.current.height = nameRef.current.height + 12 * aspect;
        nameBoxRef.current.position.set(nameRef.current.position.x, nameRef.current.position.y);

        if (isScreenShare.current) {
          nameRef.current.visible = false;
        }
      }

      //ミュートボタン
      if (muteButtonRef.current != null) {
        muteButtonRef.current.height = nameRef.current.height;
        muteButtonRef.current.width = nameRef.current.height;
        muteButtonRef.current.anchor.set(0.5, 0.5);

        //少しだけずらす幅を調整
        var adjustWidth = muteButtonRef.current.width * 0.7;

        //ミュートではない場合adjustWidthは0
        if (isMute.current == false) {
          adjustWidth = 0;
          muteButtonRef.current.visible = false;
        } else {
          muteButtonRef.current.visible = true;
        }
        if (isScreenShare.current) {
          muteButtonRef.current.visible = false;
        }
        muteButtonRef.current.position.set(x - nameWidth / 2 - adjustWidth, nameRef.current.y);

        //名前ボックスも広げる
        nameBoxRef.current.width += adjustWidth;
        nameBoxRef.current.position.set(
          nameBoxRef.current.position.x - adjustWidth / 2,
          nameBoxRef.current.position.y,
        );
      }

      //録画中ボタン
      if (recordingRef.current != null) {
        //recordingRef.current.scale.set(1);
        recordingRef.current.height = nameRef.current.height;
        recordingRef.current.width = nameRef.current.height;
        recordingRef.current.anchor.set(0.5, 0.5);

        //少しだけずらす幅を調整
        var adjustWidth = recordingRef.current.width * 0.7;

        //録画中ではない場合adjustWidthは0
        if (isRecording.current == false) {
          adjustWidth = 0;
          recordingRef.current.visible = false;
        } else {
          recordingRef.current.visible = true;
        }

        if (isScreenShare.current) {
          recordingRef.current.visible = false;
        }

        var recordX = x - nameWidth / 2 - adjustWidth;
        //ミュートボタンがある場合は位置をずらす
        if (muteButtonRef.current.visible) {
          recordX = recordX - muteButtonRef.current.width * 1.2;
          adjustWidth *= 1.5;
        }

        recordingRef.current.position.set(recordX, nameBoxRef.current.position.y);

        //名前ボックスも広げる
        nameBoxRef.current.width += adjustWidth;
        nameBoxRef.current.position.set(
          nameBoxRef.current.position.x - adjustWidth / 2,
          nameBoxRef.current.position.y,
        );
      }

      //誰が使えるかアイコン
      if (whoCanUseRef.current != null) {
        whoCanUseRef.current.height = nameRef.current.height;
        whoCanUseRef.current.width = nameRef.current.height;
        whoCanUseRef.current.anchor.set(0.5, 0.5);

        //少しだけずらす幅を調整
        var adjustWidth = whoCanUseRef.current.width * 0.7;

        //録画中ではない場合adjustWidthは0
        // if (isRecording.current == false) {
        //   adjustWidth = 0;
        //   whoCanUseRef.current.visible = false;
        // } else {
        //   whoCanUseRef.current.visible = true;
        // }

        whoCanUseRef.current.visible = true;

        if (isScreenShare.current) {
          whoCanUseRef.current.visible = false;
        }

        var recordX = x - nameWidth / 2 - adjustWidth;
        //ミュートボタンがある場合は位置をずらす
        if (muteButtonRef.current.visible) {
          recordX = recordX - muteButtonRef.current.width * 1.2;
          adjustWidth *= 1.5;
        }

        //録画ボタンがある場合は位置をずらす
        if (recordingRef.current.visible) {
          recordX = recordX - recordingRef.current.width * 1.2;
          adjustWidth *= 1.5;
        }

        whoCanUseRef.current.position.set(recordX, nameBoxRef.current.position.y);

        //名前ボックスも広げる
        nameBoxRef.current.width += adjustWidth;
        nameBoxRef.current.position.set(
          nameBoxRef.current.position.x - adjustWidth / 2,
          nameBoxRef.current.position.y,
        );
      }

      //サイズ変更ボタン
      if (changeSizeRef.current != null && modelRef.current != null) {
        changeSizeRef.current.height = 35;
        changeSizeRef.current.width = 130;
        changeSizeRef.current.anchor.set(0.5, 0.5);

        changeSizeRef.current.position.set(
          x,
          modelRef.current.position.y - modelRef.current.height / 2,
        );
      }

      //マウスオーバー時の挙動をアップデート
      baseRef.current.mouseover = function () {
        onMouseOver();
      };
    }

    /*--------------------------------- レンダー処理 --------------------------------------- */

    return (
      <>
        {isMe || isFaceScreen ? (
          <>
            {webcamState ? (
              <Live2dDetectMe modelRef={modelRef} isHairCheck={isHairCheck} />
            ) : (
              <Live2dDetectVoiceMe modelRef={modelRef} />
            )}
          </>
        ) : (
          <>
            {isFaceTracking ? (
              <Live2dDetectOther id={id} modelRef={modelRef} />
            ) : (
              <Live2dDetectVoiceOther id={id} modelRef={modelRef} />
            )}
          </>
        )}

        {/* リアクション */}
        <Reaction app={app} id={id} isMe={isMe} eachWidthRef={eachWidthRef} modelRef={modelRef} />

        {/* 音声再生 */}
        <AudioStream id={id} isMe={isMe} />
      </>
    );
  },
);

export default Live2dAvatar;
